import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useWaitForPrevious } from "@redotech/react-util/wait-for-previous";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getConversationsCounts } from "@redotech/redo-merchant-app-common/client/team";
import { MerchantAppTopic } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server";
import { MerchantAppEventServerContext } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  ConversationFiltersV3,
  FiltersStatus,
} from "@redotech/redo-model/conversation-filters/conversation-filters";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { FrontendTeamNoticeEvent } from "@redotech/redo-model/team-notifications/team-notifications";
import { sinkPromise } from "@redotech/util/promise";
import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { maybeRunConversationsRefresh } from "../handle-notice-event";
import { ActiveViewContext } from "./active-view-context";
import { FinalizedFiltersContext } from "./filters-context";

export const ActiveViewConversationCountsContext = createContext<
  Record<string, number> | undefined
>({});

export const ActiveViewConversationCountsProvider = memo(
  function ConversationCountsProvider({
    children,
    blockCountRefreshes,
  }: {
    blockCountRefreshes: boolean;
    children: ReactNode | ReactNode[];
  }) {
    const client = useRequiredContext(RedoMerchantClientContext);
    const eventServer = useRequiredContext(MerchantAppEventServerContext);
    const activeFilters = useContext(FinalizedFiltersContext);
    const activeView = useContext(ActiveViewContext);
    const activeViewName = activeView.name;
    const team = useRequiredContext(TeamContext);

    const hasSupportPermission = useHasPermission(
      SupportPermissions.VIEW_SUPPORT,
    );

    const supportEnabled = team?.settings.support?.enabled;
    const shouldLoadConversationCounts = supportEnabled && hasSupportPermission;

    useEffect(() => {
      if (!shouldLoadConversationCounts) {
        return;
      }

      const unlistenCallback = eventServer.subscribe({
        topic: MerchantAppTopic.TEAM,
        callback: async (message: FrontendTeamNoticeEvent) => {
          if (blockCountRefreshes) {
            return;
          }

          maybeRunConversationsRefresh(
            message,
            fetchConversationCountsIgnoreResult,
          );
        },
      });
      return () => unlistenCallback();
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blockCountRefreshes, shouldLoadConversationCounts]);

    useEffect(() => {
      sinkPromise(fetchConversationCounts());
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    const [conversationCounts, setConversationCounts] = useState<
      Record<string, number>
    >({});

    const [lastNotificationFetchTime, setLastNotificationFetchTime] =
      useState(0);
    const MIN_NOTIFICATION_FETCH_INTERVAL = 2000; // 2 second minimum between notification-triggered requests

    const fetchConversationCounts = useHandler(async () => {
      if (!shouldLoadConversationCounts) {
        return;
      }

      const closedFilter = { ...activeFilters, status: FiltersStatus.CLOSED };
      const openFilter = { ...activeFilters, status: FiltersStatus.OPEN };
      const snoozedFilter = { ...activeFilters, status: FiltersStatus.SNOOZED };
      const inProgressFilter = {
        ...activeFilters,
        status: FiltersStatus.IN_PROGRESS,
      };

      const currentViewFilters: {
        name: string;
        filters: ConversationFiltersV3;
      }[] = [
        { name: `${activeViewName}-closed`, filters: closedFilter },
        { name: `${activeViewName}-open`, filters: openFilter },
        { name: `${activeViewName}-snoozed`, filters: snoozedFilter },
        ...(team.settings.support?.useInProgressStatus
          ? [
              {
                name: `${activeViewName}-in_progress`,
                filters: inProgressFilter,
              },
            ]
          : []),
      ];

      const rawConversationCounts = await getConversationsCounts(client, {
        views: currentViewFilters,
      });

      const counts = {
        ...rawConversationCounts,
        [`${activeViewName}-all`]: Object.values(rawConversationCounts).reduce(
          (acc, val) => acc + val,
          0,
        ),
      };

      setConversationCounts(counts);
    });

    const throttledFetchConversationCounts = useWaitForPrevious(
      fetchConversationCounts,
    );

    const throttledFetchFromNotifications = useCallback(async () => {
      const now = Date.now();
      if (now - lastNotificationFetchTime < MIN_NOTIFICATION_FETCH_INTERVAL) {
        return;
      }
      setLastNotificationFetchTime(now);
      await throttledFetchConversationCounts();
    }, [lastNotificationFetchTime, throttledFetchConversationCounts]);

    const fetchConversationCountsIgnoreResult = useHandler(() => {
      sinkPromise(throttledFetchFromNotifications());
    });

    return (
      <ActiveViewConversationCountsContext.Provider value={conversationCounts}>
        {children}
      </ActiveViewConversationCountsContext.Provider>
    );
  },
);
